/* eslint-disable react-refresh/only-export-components */
import {
  DimeResizablePanelGroup,
  DimeResizablePanel,
  DimeResizableHandle,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/resizable')({
  component: ResizableDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="rounded-lg border border-border bg-background">{children}</div>;
}

function Panel({ label }: Readonly<{ label: string }>) {
  return (
    <div className="flex h-full items-center justify-center p-6 text-sm font-medium text-foreground">
      {label}
    </div>
  );
}

function ResizableDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Resizable</h1>
      <p className="mb-8 text-muted-foreground">
        Draggable split panes built on react-resizable-panels. Use{' '}
        <code className="font-mono text-sm">orientation</code> on the panel group and drag the
        handle to resize.
      </p>

      <DocsSection title="Handle">
        <Preview>
          <div className="h-48">
            <DimeResizablePanelGroup orientation="horizontal" className="rounded-lg border">
              <DimeResizablePanel defaultSize={50} minSize={20}>
                <Panel label="One" />
              </DimeResizablePanel>
              <DimeResizableHandle withHandle />
              <DimeResizablePanel defaultSize={50} minSize={20}>
                <Panel label="Two" />
              </DimeResizablePanel>
            </DimeResizablePanelGroup>
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use the <code className="font-mono">withHandle</code> prop on{' '}
          <code className="font-mono">DimeResizableHandle</code> to show a visible grip on the
          divider. Omit it for a plain draggable edge.
        </p>
      </DocsSection>

      <DocsSection title="Combined (nested groups)">
        <Preview>
          <div className="h-[32rem]">
            <DimeResizablePanelGroup orientation="horizontal" className="rounded-lg border">
              <DimeResizablePanel defaultSize={50} minSize={20}>
                <Panel label="Sidebar" />
              </DimeResizablePanel>
              <DimeResizableHandle withHandle />
              <DimeResizablePanel defaultSize={50} minSize={20}>
                <DimeResizablePanelGroup orientation="vertical">
                  <DimeResizablePanel defaultSize={60} minSize={20}>
                    <Panel label="Content" />
                  </DimeResizablePanel>
                  <DimeResizableHandle withHandle />
                  <DimeResizablePanel defaultSize={40} minSize={20}>
                    <Panel label="Console" />
                  </DimeResizablePanel>
                </DimeResizablePanelGroup>
              </DimeResizablePanel>
            </DimeResizablePanelGroup>
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Nest a vertical <code className="font-mono">DimeResizablePanelGroup</code> inside a panel
          of a horizontal group to build IDE-style layouts. Pass{' '}
          <code className="font-mono">withHandle</code> on each{' '}
          <code className="font-mono">DimeResizableHandle</code> to show a draggable grip.
        </p>
      </DocsSection>
    </div>
  );
}
